
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref,
  PropType
} from "vue";
import { useToast } from "../../modules/message";
import { useStorage } from "../../modules/storage";
import {
  useOrcamentoService,
  PendenciasRenovacaoPayload,
  PendenciaAetDnitPayload
} from "../../modules/business/orcamento/orcamentoBusiness";

interface FormArt {
  art: string;
  dataVencimento: Date | null;
}

interface FormDnit {
  numeroAet: string;
  dtInicial: Date | null;
  dtFinal: Date | null;
}

interface UploadDocsState {
  stateReady: boolean;
  pendencias: PendenciasRenovacaoPayload | null;
  formArt: FormArt;
  formsDnit: Array<FormDnit>;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<UploadDocsState>({
      stateReady: false,
      pendencias: null,
      formArt: { art: "", dataVencimento: null },
      formsDnit: [],
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const stateLoading = computed(
      () => state.loadings.length !== 0 || props.parentLoading
    );

    const pendenciasAetDnit = computed<Array<PendenciaAetDnitPayload>>(
      () => state.pendencias?.pendenciasAetDnit ?? []
    );

    const carregarPendencias = (): Promise<void> => {
      const { getPendenciasRenovacao, loading, data } = useOrcamentoService();
      state.loadings.push(loading);
      return getPendenciasRenovacao(props.idOrcamento)
        .then(() => {
          state.pendencias = data.value as PendenciasRenovacaoPayload;
          state.formsDnit = (
            state.pendencias.pendenciasAetDnit ?? []
          ).map(() => ({ numeroAet: "", dtInicial: null, dtFinal: null }));
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.stateReady = true;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const fazerUploadTemp = (
      inputId: string,
      msgErro: string
    ): Promise<string> => {
      return new Promise((resolve, reject) => {
        const fileInput = document.querySelector(
          "#" + inputId
        ) as HTMLInputElement;
        if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
          reject(new Error(msgErro));
          return;
        }
        const formData = new FormData();
        formData.append("file", fileInput.files[0]);
        const storage = useStorage();
        state.loadings.push(storage.loading);
        storage
          .uploadTempFile(formData)
          .then((key: string) => {
            state.loadings.splice(state.loadings.indexOf(storage.loading), 1);
            resolve(key);
          })
          .catch((err) => {
            state.loadings.splice(state.loadings.indexOf(storage.loading), 1);
            reject(err);
          });
      });
    };

    const submitUploadArt = () => {
      if (!state.formArt.art || !state.formArt.dataVencimento) {
        useToast().warning("Preencha número e validade da ART");
        return;
      }
      fazerUploadTemp(state.stateId + "_art", "Selecione o arquivo da ART")
        .then((key) => {
          const { uploadArtRenovacao, loading, data } = useOrcamentoService();
          state.loadings.push(loading);
          uploadArtRenovacao(props.idOrcamento, {
            keyBlDocumentoTemp: key,
            art: state.formArt.art,
            dataVencimento: state.formArt.dataVencimento as Date
          })
            .then(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
              useToast().success("ART enviada com sucesso");
              emit("after-upload", data.value);
              carregarPendencias();
            })
            .catch(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
            });
        })
        .catch((err) => {
          useToast().warning(err?.message ?? "Falha no upload do arquivo");
        });
    };

    const submitUploadDnit = (
      pendencia: PendenciaAetDnitPayload,
      idx: number
    ) => {
      const form = state.formsDnit[idx];
      if (!form.numeroAet || !form.dtInicial || !form.dtFinal) {
        useToast().warning(
          "Preencha número, início e fim da validade da AET DNIT"
        );
        return;
      }
      fazerUploadTemp(
        state.stateId + "_dnit_" + idx,
        "Selecione o arquivo da AET DNIT"
      )
        .then((key) => {
          const {
            uploadAetDnitRenovacao,
            loading,
            data
          } = useOrcamentoService();
          state.loadings.push(loading);
          uploadAetDnitRenovacao(props.idOrcamento, {
            idLicencaMae: pendencia.idLicencaMae,
            keyBlDocumentoTemp: key,
            numeroAet: form.numeroAet,
            dtInicial: form.dtInicial as Date,
            dtFinal: form.dtFinal as Date
          })
            .then(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
              useToast().success(
                `AET DNIT do cavalo ${pendencia.placaCavalo ?? ""} enviada`
              );
              emit("after-upload", data.value);
              carregarPendencias();
            })
            .catch(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
            });
        })
        .catch((err) => {
          useToast().warning(err?.message ?? "Falha no upload do arquivo");
        });
    };

    onMounted(() => {
      carregarPendencias();
    });

    return {
      stateLoading,
      pendenciasAetDnit,
      submitUploadArt,
      submitUploadDnit,
      ...toRefs(state)
    };
  },
  props: {
    idOrcamento: {
      type: Number,
      required: true
    },
    parentLoading: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  }
});
